import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@material-ui/core';

import Stack from 'components/shared/Stack';

import SentimentSatisfiedIcon from '@material-ui/icons/SentimentSatisfied';
import SentimentVeryDissatisfiedIcon from '@material-ui/icons/SentimentVeryDissatisfied';
import ThumbsUpDownIcon from '@material-ui/icons/ThumbsUpDown';
import { DropDowns } from 'utils/constants';
import { ConciergeActions, ConciergeRatingSentiments } from 'typings';
import { apiRateActivity } from 'state/lead.state';
import { RootState } from '../../../state/store';

interface RateActivityProps {
  activity: any;
}

interface RateActivityDialogProps {
  activity: any;
  open: boolean;
  onClose: (event: any) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  caption: {
    fontWeight: 500,
    color: theme.palette.grey['500'],
    display: 'inline-flex',
    alignItems: 'center',
  },
  issue: (props: any) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 'var(--s-2)',
    textTransform: 'none',
    height: theme.spacing(1),
    padding: 0,
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'inherit',
    },
    '& svg': {
      display: 'inline-flex',
      width: theme.spacing(1.5),
      height: theme.spacing(1.5),
      fill: props.activity.isRated ? theme.palette.grey['500'] : 'none',
      stroke: !props.activity.isRated ? theme.palette.grey['500'] : 'none',
    },
  }),
  radioGroup: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

const RateActivityDialog = ({ activity, open, onClose }: RateActivityDialogProps) => {
  const dispatch = useDispatch();
  const classes = useStyles({ activity, open, onClose });
  const conciergeRatingCategories = useSelector(
    (state: RootState) => state.concierge?.conciergeRatingCategories || []
  );

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      comment: '',
      conciergeRatingSentimentId: ConciergeRatingSentiments.GOOD,
      conciergeRatingSourceId: '',
      conciergeRatingCategoryId: '',
    },
  });

  const conciergeRatingSentimentId = watch('conciergeRatingSentimentId');

  const onSubmit = (formData: any) => {
    const data = { ...formData };

    switch (activity.commType) {
      case ConciergeActions.CALL:
        data.callId = activity.id;
        break;
      case ConciergeActions.EMAIL:
        data.emailId = activity.id;
        break;
      case ConciergeActions.SMS:
        data.messageId = activity.id;
        break;
      case ConciergeActions.NOTE:
        data.noteId = activity.id;
        break;
      default:
        return;
    }

    // IDs must be sent as numbers, which form control value does not allow
    data.conciergeRatingSentimentId = Number.parseInt(formData.conciergeRatingSentimentId, 10);
    data.conciergeRatingSourceId = Number.parseInt(formData.conciergeRatingSourceId, 10);
    data.conciergeRatingCategoryId = formData.conciergeRatingCategoryId
      ? Number.parseInt(formData.conciergeRatingCategoryId, 10)
      : undefined;

    dispatch(apiRateActivity({ data, communicationId: activity.id, type: activity.commType }));
    reset();
    onClose(null);
  };

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>Please provide feedback with your rating</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <Controller
              as={
                <RadioGroup row className={classes.radioGroup}>
                  <FormControlLabel
                    value={ConciergeRatingSentiments.GOOD}
                    control={<Radio color="primary" checkedIcon={<SentimentSatisfiedIcon />} />}
                    label="Good"
                    labelPlacement="bottom"
                  />
                  <FormControlLabel
                    value={ConciergeRatingSentiments.BAD}
                    control={
                      <Radio color="primary" checkedIcon={<SentimentVeryDissatisfiedIcon />} />
                    }
                    label="Bad"
                    labelPlacement="bottom"
                  />
                </RadioGroup>
              }
              name="conciergeRatingSentimentId"
              rules={{ required: true }}
              control={control}
            />
            <FormControl error={!!errors?.conciergeRatingSourceId}>
              <InputLabel required>Rating Source</InputLabel>
              <Controller
                as={
                  <Select>
                    {DropDowns.conciergeRatingSources.map((source) => (
                      <MenuItem key={source.value} value={source.value}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                }
                name="conciergeRatingSourceId"
                rules={{ required: true }}
                control={control}
              />
              {errors?.conciergeRatingSourceId?.type === 'required' && (
                <FormHelperText>Required</FormHelperText>
              )}
            </FormControl>
            {conciergeRatingSentimentId === ConciergeRatingSentiments.BAD && (
              <FormControl error={!!errors?.conciergeRatingCategoryId}>
                <InputLabel required>Defect Category</InputLabel>
                <Controller
                  as={
                    <Select>
                      {conciergeRatingCategories?.map((source) => (
                        <MenuItem key={source.id} value={source.id}>
                          {source.name}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                  name="conciergeRatingCategoryId"
                  rules={{ required: true }}
                  control={control}
                />
                {errors?.conciergeRatingCategoryId?.type === 'required' && (
                  <FormHelperText>Required</FormHelperText>
                )}
              </FormControl>
            )}
            <FormControl error={!!errors?.comment}>
              <Controller
                as={
                  <TextField
                    multiline
                    placeholder="Enter your feedback"
                    minRows={6}
                    variant="outlined"
                    error={!!errors?.comment?.type}
                  />
                }
                name="comment"
                rules={{ required: true }}
                control={control}
              />
              {errors?.comment?.type === 'required' && <FormHelperText>Required</FormHelperText>}
            </FormControl>
            <Button type="submit" variant="contained" color="primary">
              submit
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RateActivity = ({ activity }: RateActivityProps) => {
  const classes = useStyles({ activity });
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Typography className={classes.caption} variant="caption">
      <RateActivityDialog open={open} onClose={handleClose} activity={activity} />
      <IconButton className={classes.issue} onClick={handleClickOpen} disabled={!!activity.isRated}>
        <ThumbsUpDownIcon />
      </IconButton>
    </Typography>
  );
};

export default RateActivity;
